import { useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Calculator, Printer, DollarSign, Percent, Calendar, Hash, Building2, FileText, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Amortization() {
  const { token } = useAuth();
  const printRef = useRef(null);

  // Form state
  const [lender, setLender] = useState("");
  const [description, setDescription] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [numberOfPayments, setNumberOfPayments] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [clientName, setClientName] = useState("");

  // Results state
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedYears, setExpandedYears] = useState({});

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(3)}%`;
  };

  const handleCalculate = async () => {
    // Build request - only include fields that have values
    const request = {
      lender,
      description,
      start_date: startDate,
    };

    // Add values only if they're filled in
    if (loanAmount && loanAmount.trim()) {
      request.loan_amount = parseFloat(loanAmount.replace(/,/g, ""));
    }
    if (paymentAmount && paymentAmount.trim()) {
      request.payment_amount = parseFloat(paymentAmount.replace(/,/g, ""));
    }
    if (interestRate && interestRate.trim()) {
      request.annual_interest_rate = parseFloat(interestRate);
    }
    if (numberOfPayments && numberOfPayments.trim()) {
      request.number_of_payments = parseInt(numberOfPayments);
    }

    // Count how many values we have
    const providedCount = [
      request.loan_amount,
      request.payment_amount,
      request.annual_interest_rate,
      request.number_of_payments,
    ].filter((v) => v !== undefined && v > 0).length;

    if (providedCount < 3) {
      toast.error("Please fill in at least 3 of the 4 fields. Leave one blank to calculate it.");
      return;
    }

    if (providedCount === 4) {
      toast.error("Leave one field blank to calculate it.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/amortization/calculate`, request, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSchedule(response.data);

      // Update form with calculated value
      if (response.data.solved_for === "payment_amount") {
        setPaymentAmount(response.data.payment_amount.toFixed(2));
        toast.success(`Calculated monthly payment: ${formatCurrency(response.data.payment_amount)}`);
      } else if (response.data.solved_for === "loan_amount") {
        setLoanAmount(response.data.loan_amount.toFixed(2));
        toast.success(`Calculated loan amount: ${formatCurrency(response.data.loan_amount)}`);
      } else if (response.data.solved_for === "annual_interest_rate") {
        setInterestRate(response.data.annual_interest_rate.toFixed(3));
        toast.success(`Calculated interest rate: ${response.data.annual_interest_rate.toFixed(3)}%`);
      } else if (response.data.solved_for === "number_of_payments") {
        setNumberOfPayments(response.data.number_of_payments.toString());
        toast.success(`Calculated term: ${response.data.number_of_payments} payments`);
      }

      // Expand all years by default
      const years = {};
      response.data.year_summaries.forEach((y) => {
        years[y.year] = true;
      });
      setExpandedYears(years);

    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to calculate schedule");
    }
    setLoading(false);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Amortization Schedule - ${lender || "Loan"}</title>
          <style>
            * { 
              box-sizing: border-box; 
              margin: 0; 
              padding: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            body { 
              font-family: Arial, sans-serif; 
              font-size: 9pt; 
              line-height: 1.3;
              padding: 0.4in;
              color: #000;
            }
            .header { 
              text-align: center; 
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 3px solid #1e40af;
            }
            .header h1 { 
              font-size: 18pt; 
              font-weight: bold;
              color: #1e40af;
              margin-bottom: 3px;
            }
            .header .client-name {
              font-size: 12pt;
              font-weight: bold;
              margin-bottom: 3px;
            }
            .header p {
              font-size: 10pt;
              color: #444;
            }
            .loan-details {
              display: table;
              width: 100%;
              margin-bottom: 12px;
              border: 1px solid #ccc;
              background-color: #f8fafc !important;
            }
            .loan-details-row {
              display: table-row;
            }
            .detail-item {
              display: table-cell;
              padding: 8px 10px;
              border-right: 1px solid #ccc;
              text-align: center;
              background-color: #f8fafc !important;
            }
            .detail-item:last-child {
              border-right: none;
            }
            .detail-label {
              font-size: 7pt;
              color: #666;
              text-transform: uppercase;
              display: block;
              margin-bottom: 2px;
            }
            .detail-value {
              font-size: 11pt;
              font-weight: bold;
              color: #1e293b;
            }
            .summary-box {
              display: table;
              width: 100%;
              margin-bottom: 15px;
              background-color: #1e40af !important;
              color: white !important;
            }
            .summary-box .loan-details-row {
              display: table-row;
            }
            .summary-item {
              display: table-cell;
              padding: 10px;
              text-align: center;
              border-right: 1px solid #3b5dc9;
              background-color: #1e40af !important;
              color: white !important;
            }
            .summary-item:last-child {
              border-right: none;
            }
            .summary-label {
              font-size: 7pt;
              opacity: 0.9;
              text-transform: uppercase;
              display: block;
              margin-bottom: 2px;
              color: #bfdbfe !important;
            }
            .summary-value {
              font-size: 12pt;
              font-weight: bold;
              color: white !important;
            }
            .year-section {
              margin-bottom: 8px;
              page-break-inside: avoid;
            }
            .year-header {
              background-color: #334155 !important;
              color: white !important;
              padding: 5px 10px;
              font-weight: bold;
              font-size: 10pt;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 8pt;
            }
            th {
              background-color: #e2e8f0 !important;
              padding: 4px 6px;
              text-align: right;
              font-weight: bold;
              border: 1px solid #cbd5e1;
            }
            th:first-child, th:nth-child(2) { text-align: left; }
            td {
              padding: 3px 6px;
              text-align: right;
              border: 1px solid #e2e8f0;
            }
            td:first-child, td:nth-child(2) { text-align: left; }
            .year-total {
              background-color: #fef3c7 !important;
              font-weight: bold;
            }
            .year-total td {
              border-top: 2px solid #f59e0b;
              background-color: #fef3c7 !important;
              color: #92400e;
            }
            .grand-total {
              margin-top: 15px;
              padding: 10px;
              border: 2px solid #166534;
              background-color: #dcfce7 !important;
            }
            .grand-total h3 {
              font-size: 11pt;
              margin-bottom: 8px;
              border-bottom: 1px solid #166534;
              padding-bottom: 5px;
              color: #166534;
            }
            .grand-total-row {
              display: flex;
              justify-content: space-between;
              padding: 3px 0;
            }
            .grand-total-row:last-child {
              font-weight: bold;
              font-size: 10pt;
              border-top: 1px solid #166534;
              padding-top: 5px;
              margin-top: 3px;
              color: #166534;
            }
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              body { padding: 0.25in; }
              .year-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const toggleYear = (year) => {
    setExpandedYears((prev) => ({ ...prev, [year]: !prev[year] }));
  };

  const getPaymentsByYear = (year) => {
    if (!schedule) return [];
    return schedule.payments.filter((p) => p.payment_date.startsWith(year.toString()));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Amortization Calculator</h1>
          <p className="text-muted-foreground">Calculate loan payments and generate amortization schedules</p>
        </div>
        {schedule && (
          <Button onClick={handlePrint} variant="outline" data-testid="print-schedule-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print Schedule
          </Button>
        )}
      </div>

      {/* Input Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-500" />
            Loan Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Client Name */}
          <div className="space-y-2">
            <Label htmlFor="clientName" className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Client Name
            </Label>
            <Input
              id="clientName"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="John & Jane Smith"
              data-testid="client-name-input"
            />
          </div>

          {/* Loan Info Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="lender" className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Lender
              </Label>
              <Input
                id="lender"
                value={lender}
                onChange={(e) => setLender(e.target.value)}
                placeholder="Bank of America, Wells Fargo, etc."
                data-testid="lender-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Description
              </Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Home mortgage, Auto loan, Equipment financing, etc."
                data-testid="description-input"
              />
            </div>
          </div>

          {/* Hint */}
          <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
            <p className="text-sm text-blue-400">
              <strong>Tip:</strong> Fill in any 3 fields and leave 1 blank. We'll calculate the missing value for you.
            </p>
          </div>

          {/* Calculation Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="loanAmount" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                Loan Amount
              </Label>
              <Input
                id="loanAmount"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                placeholder="250,000"
                data-testid="loan-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentAmount" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                Payment Amount
              </Label>
              <Input
                id="paymentAmount"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="1,500"
                data-testid="payment-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="interestRate" className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-muted-foreground" />
                Annual Interest Rate %
              </Label>
              <Input
                id="interestRate"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                placeholder="6.5"
                data-testid="interest-rate-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="numberOfPayments" className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                Number of Payments
              </Label>
              <Input
                id="numberOfPayments"
                value={numberOfPayments}
                onChange={(e) => setNumberOfPayments(e.target.value)}
                placeholder="360"
                data-testid="num-payments-input"
              />
            </div>
          </div>

          {/* Start Date */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                First Payment Date
              </Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
              />
            </div>
          </div>

          {/* Calculate Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleCalculate}
              disabled={loading}
              className="px-8"
              data-testid="calculate-btn"
            >
              {loading ? "Calculating..." : "Calculate Schedule"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Display */}
      {schedule && (
        <Card>
          <CardHeader>
            <CardTitle>Amortization Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground uppercase">Loan Amount</p>
                <p className="text-xl font-bold text-foreground">{formatCurrency(schedule.loan_amount)}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground uppercase">Monthly Payment</p>
                <p className="text-xl font-bold text-foreground">{formatCurrency(schedule.payment_amount)}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground uppercase">Interest Rate</p>
                <p className="text-xl font-bold text-foreground">{formatPercent(schedule.annual_interest_rate)}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground uppercase">Total Payments</p>
                <p className="text-xl font-bold text-foreground">{schedule.number_of_payments}</p>
              </div>
            </div>

            {/* Totals Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-blue-600 text-white rounded-lg">
              <div className="text-center">
                <p className="text-xs opacity-80 uppercase">Total Principal</p>
                <p className="text-lg font-bold">{formatCurrency(schedule.total_principal)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs opacity-80 uppercase">Total Interest</p>
                <p className="text-lg font-bold">{formatCurrency(schedule.total_interest)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs opacity-80 uppercase">Total of All Payments</p>
                <p className="text-lg font-bold">{formatCurrency(schedule.total_payments)}</p>
              </div>
            </div>

            {/* Year-by-Year Schedule */}
            <div className="space-y-4">
              {schedule.year_summaries.map((yearSummary) => (
                <div key={yearSummary.year} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleYear(yearSummary.year)}
                    className="w-full flex items-center justify-between p-3 bg-muted hover:bg-muted transition-colors"
                  >
                    <span className="font-semibold text-foreground">{yearSummary.year}</span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        {yearSummary.payment_count} payments
                      </span>
                      <span className="text-green-500 font-medium">
                        Principal: {formatCurrency(yearSummary.total_principal)}
                      </span>
                      <span className="text-orange-600 font-medium">
                        Interest: {formatCurrency(yearSummary.total_interest)}
                      </span>
                      <span className="text-foreground font-bold">
                        Balance: {formatCurrency(yearSummary.ending_balance)}
                      </span>
                      <span className="text-muted-foreground">
                        {expandedYears[yearSummary.year] ? "▼" : "▶"}
                      </span>
                    </div>
                  </button>

                  {expandedYears[yearSummary.year] && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Beginning Balance</TableHead>
                          <TableHead className="text-right">Payment</TableHead>
                          <TableHead className="text-right">Principal</TableHead>
                          <TableHead className="text-right">Interest</TableHead>
                          <TableHead className="text-right">Ending Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getPaymentsByYear(yearSummary.year).map((payment) => (
                          <TableRow key={payment.payment_number}>
                            <TableCell className="font-mono text-muted-foreground">
                              {payment.payment_number}
                            </TableCell>
                            <TableCell>
                              {new Date(payment.payment_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(payment.beginning_balance)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(payment.payment_amount)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-green-500">
                              {formatCurrency(payment.principal)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-orange-600">
                              {formatCurrency(payment.interest)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-medium">
                              {formatCurrency(payment.ending_balance)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Year Total Row */}
                        <TableRow className="bg-amber-500/10 font-semibold">
                          <TableCell colSpan={3} className="text-amber-800">
                            {yearSummary.year} Total
                          </TableCell>
                          <TableCell className="text-right font-mono text-amber-800">
                            {formatCurrency(yearSummary.total_payments)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-green-700">
                            {formatCurrency(yearSummary.total_principal)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-orange-700">
                            {formatCurrency(yearSummary.total_interest)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-amber-800">
                            {formatCurrency(yearSummary.ending_balance)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hidden Print Content */}
      {schedule && (
        <div className="hidden">
          <div ref={printRef}>
            <div className="header">
              <h1>Amortization Schedule</h1>
              {clientName && <p style={{ fontSize: "12pt", fontWeight: "bold", marginBottom: "3px" }}>Prepared for: {clientName}</p>}
              <p>{schedule.lender && `${schedule.lender} - `}{schedule.description || "Loan"}</p>
            </div>

            <div className="loan-details">
              <div className="loan-details-row">
                <div className="detail-item">
                  <span className="detail-label">Client</span>
                  <span className="detail-value">{clientName || "N/A"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Lender</span>
                  <span className="detail-value">{schedule.lender || "N/A"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Loan Amount</span>
                  <span className="detail-value">{formatCurrency(schedule.loan_amount)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Monthly Payment</span>
                  <span className="detail-value">{formatCurrency(schedule.payment_amount)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Interest Rate</span>
                  <span className="detail-value">{formatPercent(schedule.annual_interest_rate)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Term</span>
                  <span className="detail-value">{schedule.number_of_payments} mo.</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">First Payment</span>
                  <span className="detail-value">{new Date(schedule.start_date).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="summary-box">
              <div className="loan-details-row">
                <div className="summary-item">
                  <span className="summary-label">Total Principal</span>
                  <span className="summary-value">{formatCurrency(schedule.total_principal)}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Total Interest</span>
                  <span className="summary-value">{formatCurrency(schedule.total_interest)}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Total of All Payments</span>
                  <span className="summary-value">{formatCurrency(schedule.total_payments)}</span>
                </div>
              </div>
            </div>

            {schedule.year_summaries.map((yearSummary) => (
              <div key={yearSummary.year} className="year-section">
                <div className="year-header">{yearSummary.year}</div>
                <table>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>#</th>
                      <th style={{ textAlign: "left" }}>Date</th>
                      <th>Beginning Balance</th>
                      <th>Payment</th>
                      <th>Principal</th>
                      <th>Interest</th>
                      <th>Ending Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaymentsByYear(yearSummary.year).map((payment) => (
                      <tr key={payment.payment_number}>
                        <td style={{ textAlign: "left" }}>{payment.payment_number}</td>
                        <td style={{ textAlign: "left" }}>
                          {new Date(payment.payment_date).toLocaleDateString()}
                        </td>
                        <td>{formatCurrency(payment.beginning_balance)}</td>
                        <td>{formatCurrency(payment.payment_amount)}</td>
                        <td>{formatCurrency(payment.principal)}</td>
                        <td>{formatCurrency(payment.interest)}</td>
                        <td>{formatCurrency(payment.ending_balance)}</td>
                      </tr>
                    ))}
                    <tr className="year-total">
                      <td colSpan={3} style={{ textAlign: "left" }}>
                        <strong>{yearSummary.year} Total</strong>
                      </td>
                      <td><strong>{formatCurrency(yearSummary.total_payments)}</strong></td>
                      <td><strong>{formatCurrency(yearSummary.total_principal)}</strong></td>
                      <td><strong>{formatCurrency(yearSummary.total_interest)}</strong></td>
                      <td><strong>{formatCurrency(yearSummary.ending_balance)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}

            <div className="grand-total">
              <h3>Loan Summary</h3>
              <div className="grand-total-row">
                <span>Total Principal Paid</span>
                <span>{formatCurrency(schedule.total_principal)}</span>
              </div>
              <div className="grand-total-row">
                <span>Total Interest Paid</span>
                <span>{formatCurrency(schedule.total_interest)}</span>
              </div>
              <div className="grand-total-row">
                <span>Total of All Payments</span>
                <span>{formatCurrency(schedule.total_payments)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
